import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import { MAX_SELECTABLE_INSTITUTIONS } from "../../../constants/institutionSelection";
import { useAuth } from "../../../hooks/useAuth";
import {
    deleteAccount,
    getInstitutionsWithDatasets,
    InstitutionWithDatasets,
} from "../../../services";
import { Button } from "../../catalyst/button";
import {
    Dialog,
    DialogActions,
    DialogDescription,
    DialogTitle,
} from "../../catalyst/dialog";
import { PageLayout } from "../../common";
import { BottomActionButton } from "../../ui";
import Icon from "../../ui/Icon";

const DataTransferAgreementScreen: React.FC = () => {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [publicInstitutions, setPublicInstitutions] = useState<
    InstitutionWithDatasets[]
  >([]);
  const [hospitals, setHospitals] = useState<InstitutionWithDatasets[]>([]);
  const [selectedInstitutions, setSelectedInstitutions] = useState<Set<string>>(
    new Set()
  );
  const [isLoading, setIsLoading] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isHospitalsExpanded, setIsHospitalsExpanded] = useState(false);
  const [isBackDialogOpen, setIsBackDialogOpen] = useState(false);
  const [isWithdrawing, setIsWithdrawing] = useState(false);

  // 기관 목록 불러오기 (공공기관 + 의료기관)
  useEffect(() => {
    loadInstitutions();
  }, []);

  const loadInstitutions = async () => {
    try {
      setIsLoading(true);
      const institutionsData = await getInstitutionsWithDatasets();
      setPublicInstitutions(institutionsData.publicInstitutions);
      setHospitals(institutionsData.hospitals);
    } catch (error) {
      console.error("기관 목록 로딩 오류:", error);
      toast.error("기관 목록을 불러오는데 실패했습니다.");
    } finally {
      setIsLoading(false);
    }
  };

  // 체크박스 토글
  const handleToggle = (institutionNo: string) => {
    setSelectedInstitutions((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(institutionNo)) {
        newSet.delete(institutionNo);
      } else {
        if (newSet.size >= MAX_SELECTABLE_INSTITUTIONS) {
          toast.error(
            `전자서명은 최대 ${MAX_SELECTABLE_INSTITUTIONS}개 기관까지 선택할 수 있습니다.`
          );
          return prev;
        }
        newSet.add(institutionNo);
      }
      return newSet;
    });
  };

  // 의료기관 전체 선택: 전체 선택해도 합산 최대 50개까지만 선택 (나머지는 무시)
  const handleSelectAllHospitals = () => {
    setSelectedInstitutions((prev) => {
      const newSet = new Set(prev);
      const prevSize = newSet.size;
      let hitLimitWithMoreLeft = false;
      for (const hospital of hospitals) {
        if (newSet.size >= MAX_SELECTABLE_INSTITUTIONS) {
          if (!newSet.has(hospital.provideInstitutionNo)) {
            hitLimitWithMoreLeft = true;
          }
          break;
        }
        newSet.add(hospital.provideInstitutionNo);
      }
      const grew = newSet.size > prevSize;
      if (
        hitLimitWithMoreLeft &&
        grew &&
        newSet.size === MAX_SELECTABLE_INSTITUTIONS
      ) {
        toast.error(
          `전자서명은 최대 ${MAX_SELECTABLE_INSTITUTIONS}개 기관까지 선택할 수 있습니다. 상한까지만 선택되었습니다.`
        );
      }
      return newSet;
    });
  };

  // 의료기관 전체 해제
  const handleDeselectAllHospitals = () => {
    setSelectedInstitutions((prev) => {
      const newSet = new Set(prev);
      hospitals.forEach((hospital) => {
        newSet.delete(hospital.provideInstitutionNo);
      });
      return newSet;
    });
  };

  // 의료기관이 모두 선택되었는지 확인
  const allHospitalsSelected =
    hospitals.length > 0 &&
    hospitals.every((hospital) =>
      selectedInstitutions.has(hospital.provideInstitutionNo)
    );

  // 의료기관이 일부 선택되었는지 확인
  const someHospitalsSelected =
    hospitals.some((hospital) =>
      selectedInstitutions.has(hospital.provideInstitutionNo)
    ) && !allHospitalsSelected;

  // 의료기관 전체 토글 (아코디언 체크박스)
  const handleToggleAllHospitals = () => {
    if (allHospitalsSelected) {
      handleDeselectAllHospitals();
      return;
    }
    // 50개 한도에 도달하면 병원을 더 추가할 수 없어 기존 분기는 무반응처럼 보임 → 병원 일괄 해제로 슬롯 확보
    const hasAnyHospitalSelected = hospitals.some((h) =>
      selectedInstitutions.has(h.provideInstitutionNo)
    );
    if (
      selectedInstitutions.size >= MAX_SELECTABLE_INSTITUTIONS &&
      hasAnyHospitalSelected
    ) {
      handleDeselectAllHospitals();
      return;
    }
    handleSelectAllHospitals();
  };

  // 다음으로 버튼 클릭
  const handleNext = async () => {
    if (selectedInstitutions.size === 0) {
      toast.error("최소 하나 이상의 기관을 선택해주세요.");
      return;
    }
    if (selectedInstitutions.size > MAX_SELECTABLE_INSTITUTIONS) {
      toast.error(
        `전자서명은 최대 ${MAX_SELECTABLE_INSTITUTIONS}개 기관까지 선택할 수 있습니다.`
      );
      return;
    }

    setIsProcessing(true);
    try {
      const allInstitutions = [...publicInstitutions, ...hospitals];
      const selected = allInstitutions.filter((inst) =>
        selectedInstitutions.has(inst.provideInstitutionNo)
      );

      navigate("/auth/mydata-request-agreement", {
        state: {
          selectedInstitutions: selected,
        },
      });
    } catch (error) {
      console.error("처리 중 오류:", error);
      toast.error("처리 중 오류가 발생했습니다.");
    } finally {
      setIsProcessing(false);
    }
  };

  /** 50개 도달 시: 이미 선택된 항목만 해제 가능, 미선택 체크박스는 비활성 */
  const isInstitutionChoiceDisabled = (institutionNo: string) =>
    selectedInstitutions.size >= MAX_SELECTABLE_INSTITUTIONS &&
    !selectedInstitutions.has(institutionNo);

  return (
    <PageLayout>
      {/* 헤더 */}
      <header className="bg-white px-4 py-3 flex items-center">
        <button
          onClick={() => setIsBackDialogOpen(true)}
          className="p-2 hover:bg-gray-100 rounded-full transition-colors"
        >
          <Icon name="chevron-left" className="text-gray-600" />
        </button>
      </header>

      {/* 중단 확인 다이얼로그 */}
      <Dialog open={isBackDialogOpen} onClose={() => setIsBackDialogOpen(false)} size="sm">
        <DialogTitle>내 의료기록 연동을 중단하시겠어요?</DialogTitle>
        <DialogDescription>
          진행 중인 내용이 저장되지 않습니다.
        </DialogDescription>
        <DialogActions>
          <Button plain onClick={() => setIsBackDialogOpen(false)} disabled={isWithdrawing}>
            취소
          </Button>
          <Button
            color="red"
            disabled={isWithdrawing}
            onClick={async () => {
              setIsWithdrawing(true);
              try {
                await deleteAccount();
                logout();
                navigate("/");
              } catch (error) {
                console.error("연동 중단 오류:", error);
                toast.error("연동 중단에 실패했습니다.");
              } finally {
                setIsWithdrawing(false);
              }
            }}
          >
            {isWithdrawing ? "처리 중..." : "중단하기"}
          </Button>
        </DialogActions>
      </Dialog>

      {isLoading ? (
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">기관 목록을 불러오는 중...</p>
          </div>
        </div>
      ) : (
        <>
          {/* 메인 콘텐츠 */}
          <main className="flex-1 overflow-y-auto px-4 pt-6 pb-6">
            {/* 안내 문구 */}
            <div className="mb-6">
              <h2 className="text-xl font-bold text-gray-900 mb-2">
                내 의료정보를 불러올 기관을 선택해주세요
              </h2>
              <p className="text-sm text-gray-600">
                전자서명은 한 번에 최대 {MAX_SELECTABLE_INSTITUTIONS}개 기관까지
                선택할 수 있습니다.
              </p>
            </div>

            <div className="space-y-3">
              {publicInstitutions.length > 0 && (
                <div className="space-y-3">
                  {publicInstitutions.map((institution) => (
                    <button
                      key={institution.provideInstitutionNo}
                      type="button"
                      disabled={isInstitutionChoiceDisabled(
                        institution.provideInstitutionNo
                      )}
                      onClick={() =>
                        handleToggle(institution.provideInstitutionNo)
                      }
                      className={`w-full p-4 rounded-lg border transition-all duration-200 text-left disabled:opacity-50 disabled:cursor-not-allowed ${
                        selectedInstitutions.has(
                          institution.provideInstitutionNo
                        )
                          ? "border-blue-600 bg-blue-50"
                          : "border-gray-200 bg-white hover:border-gray-300"
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        {/* 체크박스 */}
                        <div
                          className={`flex-shrink-0 w-5 h-5 rounded border flex items-center justify-center transition-all duration-200 ${
                            selectedInstitutions.has(
                              institution.provideInstitutionNo
                            )
                              ? "bg-blue-600 border-blue-600"
                              : "bg-white border-gray-300"
                          }`}
                        >
                          {selectedInstitutions.has(
                            institution.provideInstitutionNo
                          ) && (
                            <svg
                              className="w-4 h-4 text-white"
                              fill="none"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth="2"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                            >
                              <path d="M5 13l4 4L19 7"></path>
                            </svg>
                          )}
                        </div>

                        {/* 기관 정보 */}
                        <div className="flex-1 min-w-0">
                          <h4 className="font-semibold text-gray-900 mb-1">
                            {institution.provideInstitutionName}
                          </h4>
                          {institution.datasets.length > 0 && (
                            <div className="mt-2 flex flex-wrap gap-2">
                              {institution.datasets.map((dataset) => (
                                <span
                                  key={dataset.datasetName}
                                  className="inline-flex items-center px-2 py-1 rounded-md bg-gray-100 text-xs text-gray-700"
                                >
                                  {dataset.datasetName}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
              {/* 의료기관 섹션 - 아코디언 */}
              {hospitals.length > 0 && (
                <div className="space-y-3">
                  {/* 의료기관 전체 카드 (접기/펼치기) */}
                  <div
                    className={`rounded-lg border transition-all duration-200 ${
                      allHospitalsSelected
                        ? "border-blue-600 bg-blue-50"
                        : someHospitalsSelected
                        ? "border-blue-400 bg-blue-25"
                        : "border-gray-200 bg-white"
                    }`}
                  >
                    <div className="p-4">
                      <div className="flex items-start gap-3">
                        {/* 체크박스 */}
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleToggleAllHospitals();
                          }}
                          className="flex-shrink-0"
                        >
                          <div
                            className={`w-5 h-5 rounded border flex items-center justify-center transition-all duration-200 ${
                              allHospitalsSelected
                                ? "bg-blue-600 border-blue-600"
                                : someHospitalsSelected
                                ? "bg-blue-600 border-blue-600"
                                : "bg-white border-gray-300"
                            }`}
                          >
                            {allHospitalsSelected && (
                              <svg
                                className="w-4 h-4 text-white"
                                fill="none"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth="2"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                              >
                                <path d="M5 13l4 4L19 7"></path>
                              </svg>
                            )}
                            {someHospitalsSelected && !allHospitalsSelected && (
                              <div className="w-3 h-0.5 bg-white rounded"></div>
                            )}
                          </div>
                        </button>

                        {/* 기관 정보 */}
                        <button
                          onClick={() =>
                            setIsHospitalsExpanded(!isHospitalsExpanded)
                          }
                          className="flex-1 min-w-0 text-left"
                        >
                          <div className="flex items-start justify-between gap-3">
                            <div className="flex-1 min-w-0">
                              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                                {hospitals.length}개 병원
                              </h3>
                              {/* 공통 데이터셋 태그 */}
                              {hospitals.length > 0 &&
                                hospitals[0].datasets.length > 0 && (
                                  <div className="flex flex-wrap gap-1.5">
                                    {hospitals[0].datasets.map((dataset) => (
                                      <span
                                        key={dataset.datasetName}
                                        className="inline-flex items-center px-2 py-0.5 rounded-md bg-gray-100 text-xs text-gray-700"
                                      >
                                        {dataset.datasetName}
                                      </span>
                                    ))}
                                  </div>
                                )}
                            </div>
                            <svg
                              className={`w-5 h-5 text-gray-500 transition-transform duration-200 flex-shrink-0 ${
                                isHospitalsExpanded ? "rotate-180" : ""
                              }`}
                              fill="none"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth="2"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                            >
                              <path d="M19 9l-7 7-7-7"></path>
                            </svg>
                          </div>
                        </button>
                      </div>
                    </div>

                    {/* 펼쳐진 병원 리스트 */}
                    {isHospitalsExpanded && (
                      <div className="border-t border-gray-200 px-4 py-3 space-y-2">
                        {hospitals.map((institution) => (
                          <button
                            key={institution.provideInstitutionNo}
                            type="button"
                            disabled={isInstitutionChoiceDisabled(
                              institution.provideInstitutionNo
                            )}
                            onClick={() =>
                              handleToggle(institution.provideInstitutionNo)
                            }
                            className={`w-full p-3 rounded-lg border transition-all duration-200 text-left disabled:opacity-50 disabled:cursor-not-allowed ${
                              selectedInstitutions.has(
                                institution.provideInstitutionNo
                              )
                                ? "border-blue-500 bg-blue-50"
                                : "border-gray-200 bg-white hover:border-gray-300"
                            }`}
                          >
                            <div className="flex items-center gap-3">
                              {/* 체크박스 */}
                              <div
                                className={`flex-shrink-0 w-5 h-5 rounded border flex items-center justify-center transition-all duration-200 ${
                                  selectedInstitutions.has(
                                    institution.provideInstitutionNo
                                  )
                                    ? "bg-blue-600 border-blue-600"
                                    : "bg-white border-gray-300"
                                }`}
                              >
                                {selectedInstitutions.has(
                                  institution.provideInstitutionNo
                                ) && (
                                  <svg
                                    className="w-3 h-3 text-white"
                                    fill="none"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth="2.5"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                  >
                                    <path d="M5 13l4 4L19 7"></path>
                                  </svg>
                                )}
                              </div>

                              {/* 병원 이름만 */}
                              <div className="flex-1 min-w-0">
                                <h4 className="font-medium text-gray-900">
                                  {institution.provideInstitutionName}
                                </h4>
                              </div>
                            </div>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* 선택된 기관 수 표시 */}
            {selectedInstitutions.size > 0 && (
              <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
                <p className="text-sm text-blue-800">
                  <span className="font-semibold">
                    {selectedInstitutions.size} / {MAX_SELECTABLE_INSTITUTIONS}개
                  </span>
                  의 기관이 선택되었습니다.
                </p>
              </div>
            )}
          </main>

          {/* 다음으로 버튼 */}
          <BottomActionButton
            onClick={handleNext}
            isLoading={isProcessing}
            loadingText="처리 중..."
            disabled={selectedInstitutions.size === 0}
          >
            다음으로
          </BottomActionButton>
        </>
      )}
    </PageLayout>
  );
};

export default DataTransferAgreementScreen;
